import { resolveBackendEndpoint } from "@/lib/backend-endpoints";
import { authService } from "@/lib/authService";
import type { Snapshot, ContactDraft, PreviewDraft } from "./model";
import { z } from "zod";
import type { FieldErrors } from "./validation";
const fieldErrorsSchema = z.object({
  name: z.object({ code: z.string(), message: z.string().optional() }).optional(),
  email: z.object({ code: z.string(), message: z.string().optional() }).optional(),
  phone: z.object({ code: z.string(), message: z.string().optional() }).optional(),
  business: z.object({ code: z.string(), message: z.string().optional() }).optional(),
  message: z.object({ code: z.string(), message: z.string().optional() }).optional(),
  websiteUrl: z.object({ code: z.string(), message: z.string().optional() }).optional(),
});
export class JourneyApiError extends Error {
  constructor(
    public code: string,
    public fieldErrors: FieldErrors = {},
  ) {
    super(code);
  }
}
const statusSchema = z.object({
  status: z.enum(["idle", "running", "interrupted", "ready", "failed", "not_relevant"]),
  customerId: z
    .string()
    .regex(/^[a-zA-Z0-9._-]{1,128}$/)
    .optional(),
  publicSlug: z
    .string()
    .regex(/^[a-zA-Z0-9._-]{1,128}$/)
    .optional(),
  normalizedDomain: z.string().max(253).optional(),
  websiteUrl: z.string().max(2048).optional(),
  reason: z.string().max(2000).optional(),
  retryAllowed: z.boolean().optional(),
  assistanceRequested: z.boolean().optional(),
});
export type AnalysisStatus = z.infer<typeof statusSchema>;
export function journeyEndpoint() {
  return resolveBackendEndpoint("business_onboarding");
}
export function checkpointPayload(state: Snapshot) {
  return {
    journeyId: state.journeyId,
    action: "progress",
    snapshot: {
      revision: state.revision,
      step: state.step,
      panel: state.panel,
      lastStage: state.lastStage,
      furthestStage: state.furthestStage,
      pain: state.pain,
      fit: state.fit,
      fitResult: state.fitResult,
      milestones: state.milestones,
    },
  };
}
export async function journeyRequest(
  action: string,
  state: Snapshot,
  payload: Record<string, unknown> = {},
  keepalive = false,
): Promise<AnalysisStatus | null> {
  const user = authService.getCurrentUser();
  const token = user && !user.isAnonymous ? await user.getIdToken() : null;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), action === "analyze" ? 1_260_000 : 25_000);
  try {
    const response = await fetch(journeyEndpoint(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ ...checkpointPayload(state), action, ...payload }),
      signal: controller.signal,
      keepalive,
    });
    const body = await response.json().catch(() => {
      throw new JourneyApiError(response.ok ? "invalid_response" : "unavailable");
    });
    if (!response.ok || body?.ok !== true) {
      const parsed = fieldErrorsSchema.safeParse(body?.error?.details?.fieldErrors);
      throw new JourneyApiError(
        response.status >= 500
          ? "unavailable"
          : typeof body?.error?.code === "string"
            ? body.error.code
            : "unavailable",
        response.status === 400 && parsed.success ? parsed.data : {},
      );
    }
    if (!body.data || typeof body.data !== "object") throw new JourneyApiError("invalid_response");
    if (["contact", "preview", "lead_only", "assistance"].includes(action) && body.data.acknowledged !== true)
      throw new JourneyApiError("invalid_response");
    if (!body.data.analysis) return null;
    const parsed = statusSchema.safeParse(body.data.analysis);
    if (!parsed.success) throw new JourneyApiError("invalid_response");
    return parsed.data;
  } catch (error) {
    if (error instanceof JourneyApiError) throw error;
    throw new JourneyApiError("network_error");
  } finally {
    clearTimeout(timeout);
  }
}
export const saveContact = (state: Snapshot, contact: ContactDraft) =>
  journeyRequest("contact", state, { contact });
export const savePreview = (state: Snapshot, preview: PreviewDraft) =>
  journeyRequest("preview", state, { preview });
export const bindIdentity = (state: Snapshot, proof: ContactDraft | PreviewDraft) =>
  journeyRequest("authenticate", state, { proof });
// No PII or identity claims in the last-chance public progress request. It cannot change protected fields.
export function flushProgress(state: Snapshot) {
  try {
    void fetch(journeyEndpoint(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(checkpointPayload(state)),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* Best effort only. */
  }
}

export async function loadOnboardingConfiguration(): Promise<boolean> {
  const response = await fetch(journeyEndpoint(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "configuration" }),
    signal: AbortSignal.timeout(25_000),
  });
  const body = await response.json();
  if (!response.ok || body.ok !== true) throw new JourneyApiError("configuration_unavailable");
  return z.boolean().parse(body.data?.newWebsiteAnalysisEnabled);
}
